import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LegacyButton from "../components/Atomos/Buttons/LegacyButton";
import { toastError, toastSuccess } from "../utils/toast";
import {
  applyPublicEmailUnsubscribe,
  getPublicEmailSubscriptions,
} from "../Services/PublicEmailSubscriptions";
import {
  EmailTypeKey,
  GDPR_KEY,
} from "./MyPreferences/emailSubscriptionHelpers";
import { EmailTypeMemberEnum } from "../Enums/EmailEnum";

function categoryLabel (key: string | null | undefined): string {
  if (!key) {
    return "";
  }
  return EmailTypeMemberEnum[key as EmailTypeKey] ?? key;
}

function pendingPromptCopy (opts: {
  categoryKey?: string | null;
  eventId?: string | null;
}): { title: string; body: string } | null {
  const { categoryKey, eventId } = opts;
  if (!categoryKey && !eventId) {
    return null;
  }

  if (categoryKey === GDPR_KEY) {
    return {
      title: "Are you sure you want to unsubscribe from all WSG emails?",
      body: "This will stop all communications and invitations from WSG (GDPR Data Privacy). You can change this later in Site Preferences after signing in.",
    };
  }

  if (categoryKey) {
    const label = categoryLabel(categoryKey);
    return {
      title: `Are you sure you want to unsubscribe from ${label}?`,
      body: `You will no longer receive emails for ${label}. You can change this later in Site Preferences after signing in.`,
    };
  }

  return {
    title: "Are you sure you want to unsubscribe from this event?",
    body: "You will no longer receive emails for this event. You can change this later in Site Preferences after signing in.",
  };
}

function successCopy (opts: {
  categoryKey?: string | null;
  eventId?: string | null;
}): { title: string; body: string } {
  const { categoryKey, eventId } = opts;

  if (categoryKey === GDPR_KEY) {
    return {
      title: "You have been successfully unsubscribed",
      body: "You will no longer receive newsletter, event information, and invitations from WSG. To change this later, sign in and open Site Preferences.",
    };
  }

  if (categoryKey) {
    const label = categoryLabel(categoryKey);
    return {
      title: "You have been successfully unsubscribed",
      body: `At your request, we have cancelled your subscription for ${label}. To change this later, sign in and open Site Preferences.`,
    };
  }

  if (eventId) {
    return {
      title: "You have been successfully unsubscribed",
      body: "You will no longer receive emails for this event. To change this later, sign in and open Site Preferences.",
    };
  }

  return {
    title: "You have been successfully unsubscribed",
    body: "Your unsubscribe request has been processed.",
  };
}

export default function PublicEmailSubscriptionsPage (): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = (searchParams.get("token") ?? "").trim();
  const confirmedParam = (searchParams.get("confirmed") ?? "").trim() === "1";

  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [invalid, setInvalid] = useState(!token);
  const [applyCategory, setApplyCategory] = useState<string | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const [pendingDismissed, setPendingDismissed] = useState(false);
  const [applied, setApplied] = useState(confirmedParam);

  const pendingPrompt = useMemo(() => {
    if (applied || pendingDismissed) {
      return null;
    }
    return pendingPromptCopy({
      categoryKey: applyCategory,
      eventId,
    });
  }, [applied, pendingDismissed, applyCategory, eventId]);

  const success = useMemo(() => {
    if (!applied) {
      return null;
    }
    return successCopy({
      categoryKey: applyCategory,
      eventId,
    });
  }, [applied, applyCategory, eventId]);

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getPublicEmailSubscriptions(token);
        if (cancelled) {
          return;
        }
        setEmail(data?.email ?? "");
        setApplyCategory(data?.applyCategory ?? null);
        setEventId(data?.eventId ?? null);
        setInvalid(false);
      } catch (err: any) {
        if (!cancelled) {
          setInvalid(true);
          if (err?.status !== 401) {
            toastError("Could not load email subscriptions.");
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleConfirmAccept = useCallback(async () => {
    if (!token || applying) {
      return;
    }
    setApplying(true);
    try {
      const data = await applyPublicEmailUnsubscribe(token);
      setApplyCategory(data?.applyCategory ?? applyCategory);
      setEventId(data?.eventId ?? eventId);
      setApplied(true);
      setPendingDismissed(false);
      const next = new URLSearchParams(searchParams);
      next.set("confirmed", "1");
      setSearchParams(next, { replace: true });
      toastSuccess("You have been unsubscribed.");
    } catch {
      toastError("Could not process your unsubscribe request.");
    } finally {
      setApplying(false);
    }
  }, [token, applying, searchParams, setSearchParams, applyCategory, eventId]);

  const handleConfirmCancel = useCallback(() => {
    setPendingDismissed(true);
  }, []);

  if (invalid && !loading) {
    return (
      <main
        className="min-h-[60vh] flex items-center justify-center p-6"
        style={{ background: "#f4f4f4" }}
      >
        <div
          className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center"
          role="alert"
        >
          <h1 className="text-xl font-semibold text-gray-900 mt-0 mb-4">
            Invalid link
          </h1>
          <p className="text-gray-600 text-sm leading-relaxed">
            This unsubscribe link is invalid or has expired. Please use the
            link from your email, or sign in to manage preferences under Site
            Preferences.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-[60vh] flex items-start justify-center p-6"
      style={{ background: "#f4f4f4" }}
    >
      <div className="max-w-lg w-full bg-white rounded-lg p-8">
        {loading && !pendingPrompt && !success && !pendingDismissed ? (
          <p className="text-sm text-gray-600 text-center m-0">Loading…</p>
        ) : null}

        {pendingPrompt ? (
          <div
            className="rounded border border-gray-200 px-5 py-5 text-center"
            role="dialog"
            aria-labelledby="unsubscribe-confirm-title"
            aria-describedby="unsubscribe-confirm-body"
          >
            <h1
              id="unsubscribe-confirm-title"
              className="text-xl font-semibold text-gray-900 mt-0 mb-3"
            >
              {pendingPrompt.title}
            </h1>
            <p
              id="unsubscribe-confirm-body"
              className="text-sm text-gray-700 leading-relaxed m-0 mb-5"
            >
              {pendingPrompt.body}
            </p>
            {email ? (
              <p className="text-xs text-gray-500 mt-0 mb-5">
                Account: <strong>{email}</strong>
              </p>
            ) : null}
            <div className="flex flex-wrap justify-center gap-3">
              <LegacyButton
                text="Cancel"
                onClick={handleConfirmCancel}
                disabled={applying}
                content="text"
                style="outlined"
                color="neutral"
                className="w-auto min-w-[8rem] !opacity-100"
              />
              <LegacyButton
                text={applying ? "Please wait…" : "Accept"}
                onClick={handleConfirmAccept}
                disabled={applying || loading}
                content="text"
                style="filled"
                color="primary"
                className="w-auto min-w-[8rem] !opacity-100"
              />
            </div>
          </div>
        ) : null}

        {success ? (
          <div
            className="rounded border border-green-200 bg-green-50 px-5 py-5 text-center"
            role="status"
            aria-live="polite"
          >
            <h1 className="text-xl font-semibold text-gray-900 mt-0 mb-3">
              {success.title}
            </h1>
            <p className="text-sm text-gray-700 leading-relaxed m-0">
              {success.body}
            </p>
            {email ? (
              <p className="text-xs text-gray-500 mt-3 mb-0">
                Account: <strong>{email}</strong>
              </p>
            ) : null}
          </div>
        ) : null}

        {pendingDismissed && !applied ? (
          <div
            className="rounded border border-gray-200 px-5 py-5 text-center"
            role="status"
          >
            <h1 className="text-xl font-semibold text-gray-900 mt-0 mb-3">
              No changes made
            </h1>
            <p className="text-sm text-gray-700 leading-relaxed m-0">
              You remain subscribed. To update preferences later, sign in and
              open Site Preferences.
            </p>
            {email ? (
              <p className="text-xs text-gray-500 mt-3 mb-0">
                Account: <strong>{email}</strong>
              </p>
            ) : null}
          </div>
        ) : null}

        {!loading &&
        !pendingPrompt &&
        !success &&
        !pendingDismissed &&
        !applyCategory &&
        !eventId ? (
          <div
            className="rounded border border-gray-200 px-5 py-5 text-center"
            role="alert"
          >
            <h1 className="text-xl font-semibold text-gray-900 mt-0 mb-3">
              Nothing to unsubscribe
            </h1>
            <p className="text-sm text-gray-700 leading-relaxed m-0">
              This link does not include an email category or event. Please use
              the unsubscribe link from your email, or sign in to manage Site
              Preferences.
            </p>
          </div>
        ) : null}
      </div>
    </main>
  );
}
